/**
 * AI Chat deck exporter
 *
 * Produces Markdown-ish structured text optimized for pasting into an LLM chat
 * to get deckbuilding advice. Carries enough card detail (types, keywords,
 * roles, ownership, price) for the assistant to reason without card lookups,
 * while staying token-efficient.
 *
 * Pure — no DOM or clipboard access.
 */
import { CARD_ROLE_INFO, CARD_ROLE_ORDER } from '@/lib/cardClassification';
import { formatPowerToughnessFull, getDisplayManaCost, getFullOracleText, getFullTypeLine } from '@/lib/cardUtils';
import { DECK_FORMAT_LABELS, DECK_ZONE_LABELS, DECK_ZONE_ORDER, FORMAT_RULES } from '@/lib/deckUtils';
import { formatPrice, getSimplePriceFromPrices } from '@/lib/price';
import type { CardClassification, Color, Currency, ScryfallCard } from '@/types/card';
import type { Deck, DeckZone, EnrichedDeckCard } from '@/types/deck';

import { computeDeckStatistics, DEFAULT_STATISTICS_ZONES, type DeckStatistics } from '../../decks/lib/deckStatistics';
import { groupCardsByTypeCategory, type GroupableCard } from './summaryGrouping';

// ============================================================================
// Types
// ============================================================================

export interface AiExportOptions {
  /** Emit the full pipe-delimited column set per card (default: true) */
  verbose?: boolean;
  /** Append an oracle-text appendix (default: false) */
  includeOracleText?: boolean;
  /** Include the maybeboard section (default: true) */
  includeMaybeboard?: boolean;
  /** Include the sideboard section (default: true) */
  includeSideboard?: boolean;
  /** Currency for price cells and totals (default: USD) */
  currency?: Currency;
  /** Include per-card prices and deck value totals (default: true) */
  includePrices?: boolean;
  /** Include unowned markers and ownership totals (default: true) */
  includeOwnership?: boolean;
  /** Include EDHREC popularity rank; the Game Changer marker is always kept (default: true) */
  includeEdhrecRank?: boolean;
}

type ResolvedAiExportOptions = Required<AiExportOptions>;

/** A deck entry flattened into the shape the exporter renders from */
interface AiExportCard extends GroupableCard {
  card: ScryfallCard;
  classification: CardClassification;
  /** Copies the user has to acquire: not owned, or owned but committed to another active deck */
  missing: number;
  /** Nonfoil price in the selected currency, null when unpriced */
  price: number | null;
}

// ============================================================================
// Constants
// ============================================================================

const WUBRG: Color[] = ['W', 'U', 'B', 'R', 'G'];

/** Zones rendered as sections, in order. Tokens are never exported. */
const AI_ZONE_ORDER: DeckZone[] = DECK_ZONE_ORDER.filter((zone) => zone !== 'tokens');

/** Placeholder for a column that does not apply to a card */
const EMPTY_CELL = '-';

const TYPE_BREAKDOWN_LABELS: [keyof DeckStatistics['typeBreakdown'], string][] = [
  ['creatures', 'Creatures'],
  ['planeswalkers', 'Planeswalkers'],
  ['artifacts', 'Artifacts'],
  ['enchantments', 'Enchantments'],
  ['instants', 'Instants'],
  ['sorceries', 'Sorceries'],
  ['battles', 'Battles'],
  ['lands', 'Lands'],
  ['other', 'Other'],
];

// ============================================================================
// Export
// ============================================================================

/**
 * Export a deck as AI-chat-friendly structured text.
 */
export function exportDeckForAi(deck: Deck, cards: EnrichedDeckCard[], options: AiExportOptions = {}): string {
  const resolved: ResolvedAiExportOptions = {
    verbose: options.verbose ?? true,
    includeOracleText: options.includeOracleText ?? false,
    includeMaybeboard: options.includeMaybeboard ?? true,
    includeSideboard: options.includeSideboard ?? true,
    currency: options.currency ?? 'USD',
    includePrices: options.includePrices ?? true,
    includeOwnership: options.includeOwnership ?? true,
    includeEdhrecRank: options.includeEdhrecRank ?? true,
  };
  const { includeOracleText, includeMaybeboard, includeSideboard, currency } = resolved;

  const included = cards.filter(
    (c) => c.zone !== 'tokens' && (includeMaybeboard || c.zone !== 'maybeboard') && (includeSideboard || c.zone !== 'sideboard'),
  );
  const exportCards = included.map((entry) => toExportCard(entry, currency));

  const stats = computeDeckStatistics(included);
  const countedCards = exportCards.filter((c) => DEFAULT_STATISTICS_ZONES.includes(c.zone));

  const lines: string[] = [...buildHeader(deck, stats, resolved), '', ...buildStats(stats, countedCards, resolved)];

  for (const zone of AI_ZONE_ORDER) {
    if (zone === 'maybeboard' && !includeMaybeboard) continue;
    if (zone === 'sideboard' && !includeSideboard) continue;

    const zoneCards = exportCards.filter((c) => c.zone === zone);
    if (zoneCards.length === 0) continue;

    const quantity = sumQuantity(zoneCards);
    const suffix = zone === 'maybeboard' ? ' — under consideration, not in the deck' : '';

    lines.push('', `## ${DECK_ZONE_LABELS[zone]} (${quantity})${suffix}`);

    if (zone === 'main') {
      for (const category of groupCardsByTypeCategory(zoneCards)) {
        lines.push('', `### ${category.title} (${category.count})`);
        for (const card of category.cards) {
          lines.push(formatCardLine(card, resolved));
        }
      }
      continue;
    }

    const sorted = [...zoneCards].sort((a, b) => a.card.name.localeCompare(b.card.name));
    for (const card of sorted) {
      lines.push(formatCardLine(card, resolved));
    }
  }

  if (includeOracleText) {
    lines.push('', ...buildOracleAppendix(exportCards));
  }

  return lines.join('\n').trim();
}

// ============================================================================
// Sections
// ============================================================================

function buildHeader(deck: Deck, stats: DeckStatistics, options: ResolvedAiExportOptions): string[] {
  const { verbose, currency, includePrices, includeOwnership, includeEdhrecRank } = options;
  const lines = [`# Deck: ${deck.name}`];

  if (deck.format) {
    const rules = FORMAT_RULES[deck.format];
    let line = `Format: ${DECK_FORMAT_LABELS[deck.format] ?? deck.format}`;
    if (rules) {
      line += ` — ${stats.totalCards}/${rules.min_deck_size} cards`;
      if (rules.max_deck_size !== null && stats.totalCards > rules.max_deck_size) {
        line += ` (over max ${rules.max_deck_size})`;
      }
    }
    lines.push(line);
  }

  if (includePrices) {
    lines.push(`Prices: ${currency} — nonfoil, may be outdated`);
  }

  if (includeOwnership) {
    lines.push(
      'Legend: "(unowned)" marks cards the user still has to acquire — either not in the collection, or owned but committed to another active deck. "(N unowned)" means only some copies are covered.',
    );
  }

  if (verbose) {
    const columns = [
      'qty name',
      'mana cost',
      'type',
      'color identity',
      'P/T',
      'keywords',
      'roles',
      ...(includePrices ? ['price'] : []),
      includeEdhrecRank ? 'edhrec' : 'game changer',
    ];
    lines.push(`Columns: ${columns.join(' | ')}. "${EMPTY_CELL}" means not applicable.`);
  }

  return lines;
}

function buildStats(stats: DeckStatistics, countedCards: AiExportCard[], options: ResolvedAiExportOptions): string[] {
  const { currency, includePrices, includeOwnership } = options;
  const lines = ['## Stats', `Cards: ${stats.totalCards} — ${stats.spellCount} spells, ${stats.landCount} lands`];

  lines.push(`Avg MV: ${stats.manaCurve.average} (median ${stats.manaCurve.median})`);
  lines.push(`Curve: ${stats.manaCurve.distribution.map((count, mv) => `${mv === 7 ? '7+' : mv}:${count}`).join(' ')}`);

  const pips = ([...WUBRG, 'C'] as (Color | 'C')[])
    .filter((color) => stats.colorDistribution.pips[color] > 0)
    .map((color) => `${color}:${stats.colorDistribution.pips[color]}`);
  lines.push(`Pips: ${pips.length > 0 ? pips.join(' ') : 'none'}`);
  lines.push(`Color identity: ${formatColorIdentity(unionColorIdentity(countedCards))}`);

  const types = TYPE_BREAKDOWN_LABELS.filter(([key]) => stats.typeBreakdown[key] > 0).map(
    ([key, label]) => `${label} ${stats.typeBreakdown[key]}`,
  );
  if (types.length > 0) lines.push(`Types: ${types.join(', ')}`);

  const roleCounts = countRoles(countedCards);
  const roles = CARD_ROLE_ORDER.filter((id) => roleCounts[id] > 0).map((id) => `${CARD_ROLE_INFO[id].label} ${roleCounts[id]}`);
  if (roles.length > 0) lines.push(`Roles: ${roles.join(', ')}`);

  if (includeOwnership) {
    const unownedCount = sumMissing(countedCards);
    lines.push(`Owned: ${stats.totalCards - unownedCount}/${stats.totalCards} cards (${unownedCount} unowned)`);
  }

  if (includePrices) {
    let line = `Value: ${formatPrice(
      sumValue(countedCards, (c) => c.quantity),
      currency,
    )} total`;
    if (includeOwnership) {
      line += `, ${formatPrice(
        sumValue(countedCards, (c) => c.missing),
        currency,
      )} unowned`;
    }
    lines.push(line);
  }

  return lines;
}

function buildOracleAppendix(cards: AiExportCard[]): string[] {
  const byName = new Map<string, string>();

  for (const { card, classification } of cards) {
    if (classification.is_basic_land) continue;
    if (byName.has(card.name)) continue;

    const text = getFullOracleText(card);
    if (!text) continue;

    byName.set(card.name, text.replace(/\n+/g, '; '));
  }

  if (byName.size === 0) return [];

  const entries = [...byName.entries()].sort(([a], [b]) => a.localeCompare(b));
  return ['## Oracle Text', ...entries.map(([name, text]) => `${name}: ${text}`)];
}

// ============================================================================
// Card lines
// ============================================================================

function formatCardLine(item: AiExportCard, options: ResolvedAiExportOptions): string {
  const { verbose, currency, includePrices, includeOwnership, includeEdhrecRank } = options;
  const suffix = includeOwnership ? formatOwnershipSuffix(item) : '';
  const head = `${item.quantity}x ${item.card.name}`;

  if (!verbose) return `${head}${suffix}`;

  const cells = [
    formatManaCost(item.card),
    item.type_line || EMPTY_CELL,
    formatColorIdentity(item.card.color_identity ?? []),
    formatPowerToughnessFull(item.card) ?? EMPTY_CELL,
    item.card.keywords?.length ? item.card.keywords.join(', ') : EMPTY_CELL,
    formatRoles(item.classification),
    ...(includePrices ? [item.price === null ? EMPTY_CELL : formatPrice(item.price, currency)] : []),
    includeEdhrecRank ? formatEdhrec(item.card) : item.card.game_changer ? 'GC' : EMPTY_CELL,
  ];

  return `${head} | ${cells.join(' | ')}${suffix}`;
}

function formatOwnershipSuffix({ missing, quantity }: AiExportCard): string {
  if (missing <= 0) return '';
  return missing >= quantity ? ' (unowned)' : ` (${missing} unowned)`;
}

/** Joins every face's mana cost so split/adventure/DFC costs are not truncated */
function formatManaCost(card: ScryfallCard): string {
  return getDisplayManaCost(card) || EMPTY_CELL;
}

function formatColorIdentity(colors: Color[]): string {
  const letters = WUBRG.filter((color) => colors.includes(color)).join('');
  return letters || 'C';
}

function formatRoles(classification: CardClassification): string {
  const roles = CARD_ROLE_ORDER.filter((id) => classification.active_roles?.includes(id)).map((id) => CARD_ROLE_INFO[id].shortLabel);
  return roles.length > 0 ? roles.join(', ') : EMPTY_CELL;
}

function formatEdhrec(card: ScryfallCard): string {
  const rank = card.edhrec_rank === null || card.edhrec_rank === undefined ? EMPTY_CELL : `#${card.edhrec_rank}`;
  return card.game_changer ? `${rank} GC` : rank;
}

// ============================================================================
// Helpers
// ============================================================================

function toExportCard(entry: EnrichedDeckCard, currency: Currency): AiExportCard {
  return {
    zone: entry.zone,
    quantity: entry.quantity,
    type_line: getFullTypeLine(entry.card) ?? '',
    card: entry.card,
    classification: entry.classification,
    missing: Math.min(entry.quantity, Math.max(0, entry.quantity - Math.max(0, entry.available))),
    price: getSimplePriceFromPrices(entry.card.prices, currency),
  };
}

function sumQuantity(cards: AiExportCard[]): number {
  return cards.reduce((sum, c) => sum + c.quantity, 0);
}

function sumMissing(cards: AiExportCard[]): number {
  return cards.reduce((sum, c) => sum + c.missing, 0);
}

function sumValue(cards: AiExportCard[], copies: (card: AiExportCard) => number): number {
  return cards.reduce((sum, c) => sum + (c.price ?? 0) * copies(c), 0);
}

function unionColorIdentity(cards: AiExportCard[]): Color[] {
  const colors = new Set<Color>();
  for (const { card } of cards) {
    for (const color of card.color_identity ?? []) {
      colors.add(color);
    }
  }
  return WUBRG.filter((color) => colors.has(color));
}

function countRoles(cards: AiExportCard[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const { classification, quantity } of cards) {
    for (const role of classification.active_roles ?? []) {
      counts[role] = (counts[role] ?? 0) + quantity;
    }
  }
  return counts;
}
